import React, { useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { Shield, Book, Eye, Edit, User, Mail, Users, Send, X, Check, UserPlus } from 'lucide-react';


const InviteUserModal = ({ show, handleClose, allRoles, handleInviteUser }) => {
    const [email, setEmail] = useState('');
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [emailError, setEmailError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleEmailChange = (e) => {
        const newEmail = e.target.value;
        setEmail(newEmail);

        // Clear error when user starts typing
        if (emailError) {
            setEmailError('');
        }

        // Validate email format
        if (newEmail && !validateEmail(newEmail)) {
            setEmailError('Ange en giltig e-postadress');
        }
    };

    const handleRoleChange = (roleName) => {
        if (selectedRoles.includes(roleName)) {
            setSelectedRoles(selectedRoles.filter((role) => role !== roleName));
        } else {
            setSelectedRoles([...selectedRoles, roleName]);
        }
    };

    const handleSubmit = async () => {
        // Validate form
        if (!email) {
            setEmailError('E-postadress är obligatorisk');
            return;
        }

        if (!validateEmail(email)) {
            setEmailError('Ange en giltig e-postadress');
            return;
        }

        if (selectedRoles.length === 0) {
            return; // Don't submit if no roles selected
        }

        setIsLoading(true);
        try {
            console.log('Inviting user:', email, 'with roles:', selectedRoles);
            await handleInviteUser(email, selectedRoles);
            // Reset form on success
            setEmail('');
            setSelectedRoles([]);
            setEmailError('');
        } catch (error) {
            console.error('Error inviting user:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleModalClose = () => {
        // Reset form when closing
        setEmail('');
        setSelectedRoles([]);
        setEmailError('');
        handleClose();
    };

    // Role descriptions for better UX
    const getRoleDescription = (roleName) => {
        const descriptions = {
            'Admin': 'Full tillgång till alla funktioner och inställningar',
            'Bookkeeper': 'Kan hantera bokföring och rapporter',
            'Viewer': 'Kan endast visa rapporter och data',
            'Editor': 'Kan redigera data men inte systeminställningar'
        };
        return descriptions[roleName] || 'Användarroll';
    };


    // Replace the emoji-based function with this SVG icon approach
    const getRoleIcon = (roleName) => {
        switch (roleName.toLowerCase()) {
            case 'admin':
                return <Shield className="text-indigo-600" size={20} />;
            case 'bookkeeper':
                return <Book className="text-emerald-600" size={20} />;
            case 'viewer':
                return <Eye className="text-sky-600" size={20} />;
            case 'editor':
                return <Edit className="text-amber-600" size={20} />;
            default:
                return <User className="text-gray-600" size={20} />;
        }
    };
    return (
        <>
            <style>{`
                .modern-modal .modal-content {
                    border: none;
                    border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
                    overflow: hidden;
                }
                
                .modern-modal .modal-header {
                    background: white;
                    color: #1e293b;
                    border: none;
                    border-bottom: 1px solid #e2e8f0;
                    padding: 2rem;
                    position: relative;
                }
                
                .modern-modal .modal-title {
                    font-size: 1.5rem;
                    font-weight: 600;
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }
                
                .modern-modal .btn-close {
                    background: none;
                    border: none;
                    color: #64748b;
                    opacity: 0.8;
                    font-size: 1.5rem;
                    transition: all 0.3s ease;
                }
                
                .modern-modal .btn-close:hover {
                    opacity: 1;
                    color: #374151;
                    transform: scale(1.1);
                }
                
                .modern-modal .modal-body {
                    padding: 2rem;
                    background: #f8fafc;
                }
                
                .modern-modal .modal-footer {
                    background: white;
                    border: none;
                    padding: 1.5rem 2rem;
                    display: flex;
                    gap: 1rem;
                    justify-content: flex-end;
                }
                
                .form-group-modern {
                    margin-bottom: 1.5rem;
                }
                
                .form-label-modern {
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.875rem;
                }
                
                .form-control-modern {
                    border: 2px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 0.75rem 1rem;
                    font-size: 0.875rem;
                    transition: all 0.3s ease;
                    background: white;
                }
                
                .form-control-modern:focus {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                    outline: none;
                }
                
                .form-control-modern.error {
                    border-color: #ef4444;
                    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
                }
                
                .error-message {
                    color: #ef4444;
                    font-size: 0.75rem;
                    margin-top: 0.25rem;
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                }
                
                .roles-section {
                    background: white;
                    border-radius: 12px;
                    padding: 1.5rem;
                    border: 1px solid #e2e8f0;
                }
                
                .roles-header {
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.875rem;
                }
                
                .role-item {
                    padding: 1rem;
                    border: 2px solid #e2e8f0;
                    border-radius: 8px;
                    margin-bottom: 0.75rem;
                    transition: all 0.3s ease;
                    cursor: pointer;
                    background: white;
                }
                
                .role-item:hover {
                    border-color: #3b82f6;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
                }
                
                .role-item.selected {
                    border-color: #3b82f6;
                    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
                }
                
                .role-header {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 0.25rem;
                }
                
                .role-name {
                    font-weight: 600;
                    color: #1e293b;
                    font-size: 0.875rem;
                }
                
                .role-description {
                    color: #64748b;
                    font-size: 0.75rem;
                    line-height: 1.4;
                }
                
                .role-checkbox {
                    margin-left: auto;
                    transform: scale(1.2);
                }
                
                .role-icon {
                    font-size: 1.25rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 2rem;
                    height: 2rem;
                    border-radius: 6px;
                    background: #f1f5f9;
                }
                
                .modern-button {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.5rem;
                    border-radius: 8px;
                    font-weight: 500;
                    font-size: 0.875rem;
                    border: none;
                    transition: all 0.3s ease;
                    cursor: pointer;
                    text-decoration: none;
                }
                
                .modern-button.primary {
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                    color: white;
                }
                
                .modern-button.primary:hover:not(:disabled) {
                    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
                }
                
                .modern-button.primary:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                
                .modern-button.secondary {
                    background: #f1f5f9;
                    color: #64748b;
                    border: 1px solid #e2e8f0;
                }
                
                .modern-button.secondary:hover {
                    background: #e2e8f0;
                    color: #475569;
                }
                
                .invite-summary {
                    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
                    border: 1px solid #10b981;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-top: 1rem;
                }
                
                .invite-summary-title {
                    font-weight: 600;
                    color: #059669;
                    font-size: 0.875rem;
                    margin-bottom: 0.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                
                .invite-summary-content {
                    color: #065f46;
                    font-size: 0.75rem;
                }
                
                .loading-spinner {
                    display: inline-block;
                    width: 16px;
                    height: 16px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-radius: 50%;
                    border-top-color: white;
                    animation: spin 1s ease-in-out infinite;
                }
                
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>

            <Modal show={show} onHide={handleModalClose} className="modern-modal" centered>
                <Modal.Header>
                    <Modal.Title>
                        <UserPlus size={24} />
                        Bjud in ny användare
                    </Modal.Title>
                    <Button
                        variant="link"
                        className="btn-close p-0"
                        onClick={handleModalClose}
                        aria-label="Stäng"
                    >
                        <X size={24} />
                    </Button>
                </Modal.Header>

                <Modal.Body>
                    <Form>
                        <div className="form-group-modern">
                            <label className="form-label-modern">
                                <Mail size={16} />
                                E-postadress
                            </label>
                            <Form.Control
                                type="email"
                                placeholder="exempel@företag.se"
                                value={email}
                                onChange={handleEmailChange}
                                className={`form-control-modern ${emailError ? 'error' : ''}`}
                            />
                            {emailError && (
                                <div className="error-message">
                                    <X size={12} />
                                    {emailError}
                                </div>
                            )}
                        </div>

                        <div className="roles-section">
                            <div className="roles-header">
                                <Users size={16} />
                                Välj roller ({selectedRoles.length} valda)
                            </div>

                            {allRoles.map((role) => (
                                <div
                                    key={role.role_id}
                                    className={`role-item ${selectedRoles.includes(role.role_name) ? 'selected' : ''}`}
                                    onClick={() => handleRoleChange(role.role_name)}
                                >
                                    <div className="role-header">
                                        <div className="role-icon">
                                            {getRoleIcon(role.role_name)}
                                        </div>
                                        <div className="flex-grow-1">
                                            <div className="role-name">{role.role_name}</div>
                                            <div className="role-description">
                                                {getRoleDescription(role.role_name)}
                                            </div>
                                        </div>
                                        <Form.Check
                                            type="checkbox"
                                            checked={selectedRoles.includes(role.role_name)}
                                            onChange={() => handleRoleChange(role.role_name)}
                                            className="role-checkbox"
                                        />
                                    </div>
                                </div>
                            ))}

                            {selectedRoles.length === 0 && (
                                <Alert variant="warning" className="mt-2 mb-0">
                                    <small>Du måste välja minst en roll för användaren.</small>
                                </Alert>
                            )}
                        </div>

                        {email && selectedRoles.length > 0 && (
                            <div className="invite-summary">
                                <div className="invite-summary-title">
                                    <Check size={16} />
                                    Inbjudan redo att skickas
                                </div>
                                <div className="invite-summary-content">
                                    <strong>{email}</strong> kommer att få tillgång med rollerna: {selectedRoles.join(', ')}
                                </div>
                            </div>
                        )}
                    </Form>
                </Modal.Body>

                <Modal.Footer>
                    <button
                        className="modern-button secondary"
                        onClick={handleModalClose}
                        disabled={isLoading}
                    >
                        Avbryt
                    </button>
                    <Button className='' variant="primary"
                        onClick={handleSubmit}
                        disabled={!email || !validateEmail(email) || selectedRoles.length === 0 || isLoading}>
                        {isLoading ? (
                            <>
                                <div className="loading-spinner" />
                                Skickar...
                            </>
                        ) : (
                            <>
                                <Send className="me-2" size={16} />
                                Skicka inbjudan
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default InviteUserModal;


// import React, { useState } from 'react';
// import { Modal, Form, Button } from 'react-bootstrap';



// const InviteUserModal = ({ show, handleClose, allRoles, handleInviteUser }) => {
//     const [email, setEmail] = useState('');
//     const [selectedRoles, setSelectedRoles] = useState([]);

//     const handleRoleChange = (e) => {
//         const roleName = e.target.name;
//         if (e.target.checked) {
//             setSelectedRoles([...selectedRoles, roleName]);
//         } else {
//             setSelectedRoles(selectedRoles.filter((role) => role !== roleName));
//         }
//     };

//     const handleSubmit = () => {
//         handleInviteUser(email, selectedRoles);
//     };

//     return (
//         <Modal show={show} onHide={handleClose}>
//             <Modal.Header closeButton>
//                 <Modal.Title>Bjud in ny användare</Modal.Title>
//             </Modal.Header>
//             <Modal.Body>
//                 <Form>
//                     <Form.Group controlId="inviteEmail">
//                         <Form.Label>E-postadress</Form.Label>
//                         <Form.Control
//                             type="email"
//                             placeholder="Ange e-postadress"
//                             value={email}
//                             onChange={(e) => setEmail(e.target.value)}
//                         />
//                     </Form.Group>
//                     <Form.Label className="mt-3">Välj roller</Form.Label>
//                     {allRoles.map((role) => (
//                         <Form.Group controlId={`invite-role-${role.role_name}`} key={role.role_id}>
//                             <Form.Check
//                                 type="checkbox"
//                                 label={role.role_name}
//                                 name={role.role_name}
//                                 onChange={handleRoleChange}
//                             />
//                         </Form.Group>
//                     ))}
//                 </Form>
//             </Modal.Body>
//             <Modal.Footer>
//                 <Button variant="secondary" onClick={handleClose}>Avbryt</Button>
//                 <Button variant="primary" onClick={handleSubmit}>Skicka inbjudan</Button>
//             </Modal.Footer>
//         </Modal>
//     );
// };

// export default InviteUserModal;
